"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useTimeout = void 0;
var _react = require("react");
var _logger = require("../logger");
/**
 * This setTimeout functionality wrapper uses refs to store the current
 * callback and timeout ID, thus causing no unintentional re-renders.
 * @param callback Custom logic to be called.
 * @param delay Callback execution delay in milliseconds.
 * @returns Object with set and clear methods working with the scheduled timeout.
 */
const useTimeout = initialSettings => {
  const settings = (0, _react.useRef)({
    id: undefined,
    shouldClearOnUnmount: true,
    ...initialSettings
  }).current;

  // clears the timeout
  const clear = (0, _react.useCallback)(() => {
    if (typeof window === 'object' && settings.id !== undefined) {
      window.clearTimeout(settings.id);
      (0, _logger.logDebug)(`Cleared timeout ${settings.id}.`);
      settings.id = undefined;
    }
  }, [settings]);
  const setSettings = (0, _react.useCallback)(newSettings => {
    if (typeof newSettings?.callback === 'function') {
      settings.callback = newSettings.callback;
    }
    if (typeof newSettings?.delay === 'number') {
      settings.delay = newSettings.delay;
    }
    if (typeof newSettings?.shouldClearOnUnmount === 'boolean') {
      settings.shouldClearOnUnmount = newSettings.shouldClearOnUnmount;
    }
  }, [settings]);

  // sets the timeout
  const set = (0, _react.useCallback)(newSettings => {
    clear();
    setSettings(newSettings);
    if (typeof window === 'object') {
      settings.id = window.setTimeout(() => settings.callback?.(), settings.delay);
      (0, _logger.logDebug)(`Set timeout ${settings.id}, will execute in ${settings.delay} ms.`);
    } else {
      (0, _logger.logWarning)(`Can't set timeout ATM, window object is not defined.`);
    }
  }, [clear, setSettings, settings]);

  // does cleanup on unmount by default
  (0, _react.useEffect)(() => () => {
    if (settings.shouldClearOnUnmount) {
      clear();
    }
  }, [clear, settings]);
  return {
    clear,
    set
  };
};
exports.useTimeout = useTimeout;